import { studioDateKey, studioLocalDateTimeToIso } from "@/lib/studioTime";

// Grid dates are civil Studio days carried in UTC, not appointment instants.
// UTC arithmetic keeps month/week navigation independent of the device zone.
export const calendarDayKey = date => date.toISOString().slice(0, 10);
export function studioInstant(value) {
  // Legacy ISO datetimes without an offset follow the backend UTC convention.
  const explicit = typeof value === "string" && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value)
    ? value.replace(" ", "T") + "Z" : value;
  return new Date(explicit);
}
export function calendarDate(instant = new Date(), timeZone = "Europe/Rome") {
  return new Date(`${studioDateKey({ timezone: timeZone }, studioInstant(instant))}T00:00:00Z`);
}
export function startOfDay(date) {
  const result = new Date(date); result.setUTCHours(0, 0, 0, 0); return result;
}
export function addDays(date, count) {
  const result = new Date(date); result.setUTCDate(result.getUTCDate() + count); return result;
}
export function startOfWeek(date) {
  const result = startOfDay(date); return addDays(result, -((result.getUTCDay() + 6) % 7));
}
export function startOfMonth(date) {
  const result = startOfDay(date); result.setUTCDate(1); return result;
}
export const sameDay = (a, b) => calendarDayKey(a) === calendarDayKey(b);
export function studioInput(value, timeZone = "Europe/Rome") {
  if (!value) return "";
  const date = studioInstant(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(date).map(({ type, value: part }) => [type, part]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}
export function studioMinutes(value, timeZone = "Europe/Rome") {
  const input = studioInput(value, timeZone);
  return Number(input.slice(11, 13)) * 60 + Number(input.slice(14, 16));
}
export function nextStudioSlot(timeZone = "Europe/Rome", now = new Date()) {
  const wall = new Date(`${studioInput(now, timeZone)}:00Z`);
  wall.setUTCMinutes(wall.getUTCMinutes() + 30 - wall.getUTCMinutes() % 30);
  // Skip nonexistent/repeated DST wall times for a new default suggestion.
  for (let count = 0; count < 100; count += 1) {
    try {
      const instant = new Date(studioLocalDateTimeToIso(wall.toISOString().slice(0, 16), timeZone));
      if (instant > now) return instant;
    } catch { /* choose the next unique half-hour */ }
    wall.setUTCMinutes(wall.getUTCMinutes() + 30);
  }
  throw new RangeError("invalid_local_time");
}
export function calendarRange(anchor, view, timeZone = "Europe/Rome") {
  const from = view === "month" ? startOfWeek(startOfMonth(anchor))
    : view === "week" ? startOfWeek(anchor) : startOfDay(anchor);
  const to = addDays(from, view === "month" ? 42 : view === "week" ? 7 : 1);
  return { from, to, fromIso: studioDayStartIso(calendarDayKey(from), timeZone),
    toIso: studioDayStartIso(calendarDayKey(to), timeZone) };
}

// Midnight itself can be skipped or repeated in some IANA zones. Query a civil
// day from its first real instant, while typed appointment times remain strict.
function studioDayStartIso(day, timeZone) {
  const wall = Date.parse(`${day}T00:00:00Z`);
  const offsets = new Set([-36, 0, 36].map(hours => {
    const probe = wall + hours * 3600000;
    return Date.parse(`${studioInput(probe, timeZone)}:00Z`) - probe;
  }));
  for (let minute = 0; minute < 1440; minute += 1) {
    const input = new Date(wall + minute * 60000).toISOString().slice(0, 16);
    const candidates = [...offsets].map(offset => wall + minute * 60000 - offset)
      .filter(instant => studioInput(instant, timeZone) === input);
    if (candidates.length) return new Date(Math.min(...candidates)).toISOString();
  }
  throw new RangeError("invalid_local_date");
}
